import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSiteSettings, resolveAssetPath, type SiteSettings } from "../../api";

/** 默认 Logo */
const DEFAULT_LOGO = "https://storage.readdy-site.link/project_files/870dd70f-e20b-4d95-a3c1-abdd0663e4f5/40837718-58c9-438e-a598-fe499966453f_logo.png?v=01d6c2cfcf3b187e46eae996fab2dd4f";
/** 默认公司名称 */
const DEFAULT_COMPANY = "Quanzhou Shijia Rongyan Electronic Technology Co, Ltd.";

interface PolicyModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

function PolicyModal({ title, isOpen, onClose, children }: PolicyModalProps) {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl mx-4 w-full max-w-2xl max-h-[80vh] flex flex-col animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-dark">{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        {/* Content */}
        <div className="px-6 md:px-8 py-6 overflow-y-auto text-sm text-gray-600 leading-relaxed space-y-4">
          {children}
        </div>
        {/* Footer */}
        <div className="px-6 md:px-8 py-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  settings?: SiteSettings | null;
}

export default function Footer({ settings: propSettings }: Props) {
  const navigate = useNavigate();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [settings, setSettings] = useState<SiteSettings>(propSettings || {});

  useEffect(() => {
    if (propSettings !== undefined) return; // 父组件已传入则不再请求
    fetchSiteSettings()
      .then(setSettings)
      .catch(() => {}); // 静默失败，使用默认值
  }, [propSettings]);

  const logoSrc = resolveAssetPath(settings.site_logo) || DEFAULT_LOGO;
  const siteName = settings.site_name || "Huatera";
  const companyName = settings.company_name || DEFAULT_COMPANY;
  const copyright = settings.copyright || `© ${new Date().getFullYear()} ${companyName}. All rights reserved.`;
  const phone = settings.company_phone || "+86 159 9998 9668";
  const email = settings.company_email || "huatera88@gmail.com";
  const phoneHref = `tel:${phone.replace(/\s/g, '')}`;
  const emailHref = `mailto:${email}`;

  const leftLinks = [
    { label: "Home", path: "/" },
    { label: "About", path: "/about" },
    { label: "Products", path: "/products" },
    { label: "Solutions", path: "/solutions" },
  ];

  const rightLinks = [
    { label: "Downloads", path: "/downloads" },
    { label: "News", path: "/news" },
    { label: "Contact", path: "/contact" },
  ];

  return (
    <>
      <footer className="bg-dark rounded-t-3xl mx-2 md:mx-4 lg:mx-6 mt-16">
        <div className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
          {/* Top Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {/* Left - Brand */}
            <div className="md:col-span-1">
              <div className="mb-3">
                <img
                  src={logoSrc}
                  alt={siteName}
                  className="h-14 w-auto object-contain"
                />
              </div>
              <p className="text-gray-400 text-sm mb-6">
                {companyName}
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://www.facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-primary hover:text-white transition-colors"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a
                  href="https://www.twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-primary hover:text-white transition-colors"
                  aria-label="Twitter"
                >
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a
                  href="https://www.linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-primary hover:text-white transition-colors"
                  aria-label="LinkedIn"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a
                  href="https://www.youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-primary hover:text-white transition-colors"
                  aria-label="YouTube"
                >
                  <i className="ri-youtube-fill text-lg"></i>
                </a>
              </div>
            </div>

            {/* Middle - Links */}
            <div className="md:col-span-1">
              <h4 className="text-white text-sm font-medium mb-6">
                Quick Links
              </h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div className="space-y-3">
                  {leftLinks.map((link) => (
                    <a
                      key={link.path}
                      href={link.path}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(link.path);
                      }}
                      className="block text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
                <div className="space-y-3">
                  {rightLinks.map((link) => (
                    <a
                      key={link.path}
                      href={link.path}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(link.path);
                      }}
                      className="block text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right - Contact */}
            <div className="md:col-span-1">
              <h4 className="text-white text-sm font-medium mb-6">
                Contact Us
              </h4>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-primary"></i>
                  <a href="tel:+8613788855889" className="text-sm text-gray-400 hover:text-white transition-colors">+86 137 8885 5889</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-primary"></i>
                  <a href={phoneHref} className="text-sm text-gray-400 hover:text-white transition-colors">{phone}</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-primary"></i>
                  <a href={emailHref} className="text-sm text-gray-400 hover:text-white transition-colors">{email}</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-global-line text-primary"></i>
                  <a href="https://www.huatera.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white transition-colors">www.huatera.com</a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-12 pt-8 border-t border-gray-800">
            <p className="text-sm text-gray-500">
              {copyright}
            </p>
            <div className="flex items-center gap-6">
              <button
                onClick={() => setPrivacyOpen(true)}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => setTermsOpen(true)}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <PolicyModal
        title="Privacy Policy"
        isOpen={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
      >
        <p className="font-medium text-dark">Effective Date: June 1, 2026</p>
        <p>
          Quanzhou Shijia Rongyan Electronic Technology Co., Ltd. ("Huatera", "we", "us", or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website (www.huatera.com).
        </p>
        <h4 className="font-medium text-dark mt-2">1. Information We Collect</h4>
        <p>
          We may collect personal information that you voluntarily provide to us when you:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Fill out the contact form or send an inquiry through our website</li>
          <li>Subscribe to our newsletter or updates</li>
          <li>Request product catalogs or technical documents from our Download Center</li>
          <li>Contact us via email or phone</li>
        </ul>
        <p>
          The personal information we collect may include your name, email address, phone number, company name, and country/region. We do not collect payment information or financial data, as our website does not process online transactions.
        </p>
        <h4 className="font-medium text-dark mt-2">2. How We Use Your Information</h4>
        <p>
          We use the information we collect to:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Respond to your inquiries and provide customer support</li>
          <li>Send product information, technical updates, and promotional materials (if you have opted in)</li>
          <li>Improve our website, products, and services</li>
          <li>Comply with legal obligations and enforce our policies</li>
        </ul>
        <h4 className="font-medium text-dark mt-2">3. Information Sharing</h4>
        <p>
          We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our website and conducting our business, provided they agree to keep this information confidential.
        </p>
        <h4 className="font-medium text-dark mt-2">4. Data Security</h4>
        <p>
          We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
        </p>
        <h4 className="font-medium text-dark mt-2">5. Cookies and Tracking</h4>
        <p>
          Our website may use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and understand user preferences. You can set your browser to refuse cookies, but some features of the website may not function properly.
        </p>
        <h4 className="font-medium text-dark mt-2">6. Third-Party Links</h4>
        <p>
          Our website may contain links to third-party websites (such as our social media pages). We are not responsible for the privacy practices or content of these external sites.
        </p>
        <h4 className="font-medium text-dark mt-2">7. Your Rights</h4>
        <p>
          You have the right to access, correct, or delete your personal information. To exercise these rights, please contact us at <a href="mailto:huatera88@gmail.com" className="text-primary underline hover:text-primary-dark transition-colors">huatera88@gmail.com</a>.
        </p>
        <h4 className="font-medium text-dark mt-2">8. Changes to This Policy</h4>
        <p>
          We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.
        </p>
        <h4 className="font-medium text-dark mt-2">9. Contact Us</h4>
        <p>
          If you have any questions about this Privacy Policy, please contact us at:
        </p>
        <p>
          Email: <a href="mailto:huatera88@gmail.com" className="text-primary underline hover:text-primary-dark transition-colors">huatera88@gmail.com</a><br />
          Phone: <a href="tel:+8613788855889" className="text-primary underline hover:text-primary-dark transition-colors">+86 137 8885 5889</a> / <a href="tel:+8615999989668" className="text-primary underline hover:text-primary-dark transition-colors">+86 159 9998 9668</a>
        </p>
      </PolicyModal>

      {/* Terms of Service Modal */}
      <PolicyModal
        title="Terms of Service"
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
      >
        <p className="font-medium text-dark">Last Updated: June 1, 2026</p>
        <p>
          Please read these Terms of Service ("Terms") carefully before using the Huatera website (www.huatera.com) operated by Quanzhou Shijia Rongyan Electronic Technology Co., Ltd. ("Huatera", "we", "us", or "our").
        </p>
        <p>
          By accessing or using our website, you agree to be bound by these Terms. If you disagree with any part of the Terms, you may not access the website.
        </p>
        <h4 className="font-medium text-dark mt-2">1. Use of the Website</h4>
        <p>
          Our website is designed to provide information about our two-way radio products, solutions, and services. You may browse our product catalog, download technical documents, read news and updates, and contact us through the provided channels.
        </p>
        <p>
          You agree to use the website only for lawful purposes and in a way that does not infringe the rights of, restrict, or inhibit anyone else's use and enjoyment of the website.
        </p>
        <h4 className="font-medium text-dark mt-2">2. Intellectual Property</h4>
        <p>
          All content on this website, including but not limited to text, images, graphics, logos, product specifications, and downloadable documents, is the property of Huatera or its licensors and is protected by copyright, trademark, and other intellectual property laws.
        </p>
        <p>
          You may view, download, and print materials from the website for your personal or internal business use only. You may not reproduce, distribute, modify, create derivative works from, or commercially exploit any content without our prior written consent.
        </p>
        <h4 className="font-medium text-dark mt-2">3. Product Information</h4>
        <p>
          We make every effort to ensure that product descriptions, specifications, images, and pricing information on our website are accurate and up-to-date. However, we do not warrant that product descriptions or other content is accurate, complete, reliable, current, or error-free.
        </p>
        <p>
          Product specifications and availability are subject to change without notice. For the most current information, please contact our sales team directly.
        </p>
        <h4 className="font-medium text-dark mt-2">4. No Online Sales</h4>
        <p>
          Our website is an informational and promotional platform. We do not currently offer direct online purchasing or e-commerce functionality. All product inquiries, quotations, and orders should be directed to our sales team via email or phone.
        </p>
        <h4 className="font-medium text-dark mt-2">5. User Submissions</h4>
        <p>
          When you submit information through our contact form or inquiry channels, you grant us the right to use that information to respond to your request and for our internal business purposes. You represent that any information you provide is accurate and that you have the right to share it.
        </p>
        <h4 className="font-medium text-dark mt-2">6. Disclaimer of Warranties</h4>
        <p>
          The website and its content are provided on an "as is" and "as available" basis without any warranties of any kind, either express or implied. We do not guarantee that the website will be available at all times or that it will be free from errors, viruses, or other harmful components.
        </p>
        <h4 className="font-medium text-dark mt-2">7. Limitation of Liability</h4>
        <p>
          In no event shall Huatera, its directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the website, even if advised of the possibility of such damages.
        </p>
        <h4 className="font-medium text-dark mt-2">8. Governing Law</h4>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the People's Republic of China, without regard to its conflict of law provisions.
        </p>
        <h4 className="font-medium text-dark mt-2">9. Changes to Terms</h4>
        <p>
          We reserve the right to modify or replace these Terms at any time. It is your responsibility to review these Terms periodically for changes. Your continued use of the website after any changes constitutes acceptance of those changes.
        </p>
        <h4 className="font-medium text-dark mt-2">10. Contact Us</h4>
        <p>
          If you have any questions about these Terms, please contact us at:
        </p>
        <p>
          Email: <a href="mailto:huatera88@gmail.com" className="text-primary underline hover:text-primary-dark transition-colors">huatera88@gmail.com</a><br />
          Phone: <a href="tel:+8613788855889" className="text-primary underline hover:text-primary-dark transition-colors">+86 137 8885 5889</a> / <a href="tel:+8615999989668" className="text-primary underline hover:text-primary-dark transition-colors">+86 159 9998 9668</a>
        </p>
      </PolicyModal>
    </>
  );
}